import React, { useEffect, useState } from "react";
import { Avatar, Button } from "@mui/material";

import DocumentUpload from "components/DocumentUpload";
import { IDocument } from "models/DocumentModel";
import { Shared } from "utility/shared";
import { attachmentLinkPrimaryIcon } from "assets/images";
import ConfirmationDialog from "components/ConfirmationDialog";
import environment from "environment";
import { ContractAttachmentProps } from "./ContractAttachment.Props";
import { IContractAttachment } from "models/ContractManagement/NewContractDetailModel";
import { ContractService } from "services/contractManagement.service";
import { ModulePhase } from "utility/enums/contract-detail";

type UpdateDocumentType = {
	contractAttachmentId: number;
	file: File;
	contractId: number;
	documentDescription: string;
	documentFilePath: string;
};

const ContractAttachment: React.FC<ContractAttachmentProps> = ({
	contractId,
	requestNumber,
	isDisabled,
	stepNumber = "",
	sequenceNumber = 0,
	contractStepId,
	isAttachmentViewOnly,
	handleSaveContractDetail,
	modulePhaseId,
}) => {
	const blobUrl = environment.blobStorageUrl || "";
	const blobFilePath: string = requestNumber
		? `${blobUrl}/contract/${requestNumber}/attachment`
		: "";

	const [documentList, setDocumentList] = useState<IDocument[]>([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [openConfirmationDialog, setOpenConfirmationDialog] =
		useState<boolean>(false);
	const [message, setMessage] = useState<string>("");
	const [isFileUploaded, setIsFileUploaded] = useState(false);
	const [tempDocument, setTempDocument] =
		React.useState<UpdateDocumentType | null>(null);
	const [tempAttachmentIdList, setTempAttachmentIdList] = React.useState<
		number[]
	>([]);

	const isHoldPhase = (phaseId: number) =>
		phaseId >= ModulePhase.HoldRequest &&
		phaseId <= ModulePhase.HoldRemovalRequestApproval;

	const isTerminatePhase = (phaseId: number) =>
		phaseId >= ModulePhase.TerminationRequest &&
		phaseId <= ModulePhase.ReinstatementRequestApproval;

	const getContractAttachment = async (isInitialize = false) => {
		try {
			Shared.showLoader(true);
			let response: IContractAttachment[] = [];

			if (modulePhaseId && isHoldPhase(modulePhaseId)) {
				response = await ContractService.getContractHoldAttachmentsByStepId(
					contractStepId || 0
				);
			} else if (modulePhaseId && isTerminatePhase(modulePhaseId)) {
				response =
					await ContractService.getContractTerminationAttachmentsByStepId(
						contractStepId || 0
					);
			} else {
				response = await ContractService.getContractAttachment(
					contractId,
					contractStepId || 0
				);
			}
			const tempDocumentList: IDocument[] = [];
			response.map((r) =>
				tempDocumentList.push({
					id: r.contractAttachmentId,
					documentDescription: r.documentDescription,
					documentFileName: r.documentFileName,
					documentFilePath: `${blobUrl}/${r.documentFilePath}/${r.documentFileName}`,
					stepNumber: r.stepNumber || null,
					sequenceNumber: r.sequenceNumber || null,
					isReadOnly:
						isInitialize && stepNumber
							? true
							: !!(
									tempAttachmentIdList.findIndex(
										(id) => id === r.contractAttachmentId
									) > -1
							  ),
				})
			);
			if (isInitialize && stepNumber) {
				setTempAttachmentIdList(response.map((r) => r.contractAttachmentId));
			}
			setDocumentList(tempDocumentList);
		} catch (error) {
			setOpenDialog(false);
			Shared.showSnackbarMessage("Error loading contract attachment list");
		} finally {
			Shared.showLoader(false);
		}
	};

	useEffect(() => {
		setDocumentList([]);
		if (contractId <= -1 || !openDialog) {
			return;
		}
		setTempAttachmentIdList([]);
		getContractAttachment(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openDialog, contractId]);

	const openAttachmentDialog = async () => {
		if (contractId <= -1 && handleSaveContractDetail) {
			// save the detail before attaching the documents
			const res = await handleSaveContractDetail();
			if (res) setOpenDialog(true);
		} else setOpenDialog(true);
	};

	const handleDeleteAttachment = async (documentId: number) => {
		try {
			Shared.showLoader(true);
			await ContractService.deleteContractAttachment(documentId);
			getContractAttachment();
		} catch (error) {
			Shared.showSnackbarMessage("Error while deleting document");
		} finally {
			Shared.showLoader(false);
		}
	};

	const handleSaveAttachment = async (description: string, file: File) => {
		setIsFileUploaded(false);

		const formData = new FormData();
		formData.append("File", file);
		formData.append("ContractId", contractId.toString());
		formData.append("DocumentDescription", description);

		try {
			Shared.showLoader(true);

			if (modulePhaseId && isHoldPhase(modulePhaseId)) {
				await ContractService.saveHoldContractAttachment(
					contractStepId || 0,
					formData
				);
				await getContractAttachment();
			} else if (modulePhaseId && isTerminatePhase(modulePhaseId)) {
				await ContractService.saveTerminationContractAttachment(
					contractStepId || 0,
					formData
				);
				await getContractAttachment();
			} else {
				formData.append("documentFilePath", file.name);
				formData.append("contractStepId", contractStepId?.toString() || "");
				await ContractService.saveContractAttachment(formData);
				await getContractAttachment();
			}

			setIsFileUploaded(true);
			Shared.showSnackbarMessage("Attachment saved successfully.", "success");
		} catch (error: any) {
			if (error.badRequestError && error.responseObj) {
				setMessage(error.badRequestError);
				setTempDocument({
					contractAttachmentId: error.responseObj,
					file,
					contractId,
					documentDescription: description,
					documentFilePath: requestNumber ? `${blobFilePath}/${file.name}` : "",
				});
				setOpenConfirmationDialog(true);
			} else {
				Shared.showSnackbarMessage("Error while saving document");
			}
		} finally {
			Shared.showLoader(false);
		}
	};

	const handleConfirmationDialogClose = async (isConfirm = false) => {
		if (isConfirm) {
			// API call to update contract attachment
			const formData: FormData = new FormData();
			formData.append("file", tempDocument!.file);
			formData.append("contractId", tempDocument!.contractId.toString());
			formData.append("documentDescription", tempDocument!.documentDescription);
			try {
				Shared.showLoader(true);
				if (modulePhaseId && isHoldPhase(modulePhaseId)) {
					await ContractService.updateHoldContractAttachment(
						tempDocument!.contractAttachmentId || 0,
						formData
					);
					await getContractAttachment();
				} else if (modulePhaseId && isTerminatePhase(modulePhaseId)) {
					await ContractService.updateTerminatContractAttachment(
						tempDocument!.contractAttachmentId || 0,
						formData
					);
					await getContractAttachment();
				} else {
					formData.append("documentFilePath", tempDocument!.file.name);
					formData.append("contractStepId", contractStepId?.toString() || "");
					await ContractService.updateContractAttachment(
						tempDocument!.contractAttachmentId,
						formData
					);
				}
				getContractAttachment();
				setIsFileUploaded(true);
			} catch (error) {
				Shared.showSnackbarMessage("Error while updating document");
			} finally {
				setTempDocument(null);
				Shared.showLoader(false);
			}
		}
		setOpenConfirmationDialog(false);
		setMessage("");
		setIsFileUploaded(true);
	};

	return (
		<>
			<Button
				variant="text"
				startIcon={
					<Avatar
						sx={
							modulePhaseId
								? {
										width: "16px !important", // reduce icon size if modulePhaseId exists
										height: "16px !important",
								  }
								: {}
						}
						src={attachmentLinkPrimaryIcon}
						alt="Attach"
					/>
				}
				disabled={isDisabled}
				onClick={openAttachmentDialog}
				sx={{
					...(modulePhaseId && {
						fontSize: "0.75rem !important",
						marginBottom: "-3px !important",
						"& .MuiButton-startIcon": {
							marginRight: "5px !important", // reduce startIcon margin
						},
					}),
				}}
			>
				Attachments
			</Button>
			{openDialog && (
				<DocumentUpload
					open={openDialog}
					documentList={documentList}
					isFileUploaded={isFileUploaded}
					isDisabled={contractId === -1}
					sequenceNumber={sequenceNumber}
					stepNumber={stepNumber}
					isAttachmentViewOnly={isAttachmentViewOnly}
					handleUploadDocument={handleSaveAttachment}
					handleDeleteDocument={handleDeleteAttachment}
					handleDialogClose={() => setOpenDialog(false)}
				/>
			)}
			{openConfirmationDialog && (
				<ConfirmationDialog
					open={openConfirmationDialog}
					title="Attachment Update Confirmation"
					message={message}
					positiveActionText="Yes"
					negativeActionText="No"
					handleDialogClose={handleConfirmationDialogClose}
				/>
			)}
		</>
	);
};

export default ContractAttachment;
